import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  notification,
  Row,
  Select,
  Switch,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import styled from "styled-components";
import { cloneDeep } from "lodash";
import QuantityControl from "components/shared-components/QuantityControl";
import { DeleteOutlined } from "@ant-design/icons";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";

// Constants
import { PRODUCT_STOCK_COUNTRIES } from "../../../../../constants/ProductConstant";
import {
  ADDITIONAL_DELIVERY_LABEL,
  DEFAULT_DELIVERY_LABEL_FALLBACK,
  DEFAULT_DELIVERY_TIME,
  DELIVERY_DAYS,
  JLF_PICKED_UP,
  MORNING_DELIVERY_CHARGE,
  MORNING_DELIVERY_TIME,
  MORNING_EXTRA_CHARGE_LABELS,
  ORDER_STATUS_CREATED,
  RETAIL_DELIVERY_LABEL,
} from "../../../../../constants/OrderConstant";
import { FIRESTORE_ORDERS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE, GROWY } from "../../../../../constants/CompanyConstants";
import { CURRENCY, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { INVOICE_SURCHARGE_PERCENTAGE, JIMMY_LOVES_FOOD_ID } from "../../../../../constants/PricingConstants";

// Functions
import { formatPrice } from "../../../../../functions/formatters";
import {
  applyCustomerPricingToProduct,
  calculateOrderPricing,
  calculateOrderShippingCost,
  calculateOrderSubtotalWithoutDiscount,
  getBundleItemsWithCustomerPricing,
  getBundlePackagingPrice,
  recalculateBundleItemPricesForOverride,
} from "../../../../../functions/pricing";
import { PAYMENT_METHOD_INVOICE } from "../../../../../constants/PaymentConstants";
import dayjs from "dayjs";
import { USER_STATUS_ACTIVE } from "../../../../../constants/UserConstant";
import { getPickingDate } from "../../../../../functions/deliveryValidation";
import axios from "axios";
import urls from "../../../../../urls";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { useStockData } from "../../../../../data/hooks/firestore/useStock";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { validateDeliveryDay as validateDeliveryDayUtil } from "../../../../../functions/deliveryValidation";

const { Option } = Select;

const AddOrderModal = ({
  visible,
  onClose,
  customers,
  users,
  products,
  selectedDeliveryDate,
  order,
  companyFilter,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { email } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deliveryLabel, setDeliveryLabel] = useState(DEFAULT_DELIVERY_LABEL_FALLBACK);
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState(null);
  const [addShippingCost, setAddShippingCost] = useState(true);
  const [newOrderProducts, setNewOrderProducts] = useState([]);
  const [newProduct, setNewProduct] = useState("");
  const [productsExceedingStock, setProductsExceedingStock] = useState({});
  const [tempDeliveryDate, setTempDeliveryDate] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [manualPriceOverrides, setManualPriceOverrides] = useState({});
  const [deliverySettingsWarning, setDeliverySettingsWarning] = useState(null);
  const [deliveryDayWarning, setDeliveryDayWarning] = useState(null);
  const [currentDeliveryDate, setCurrentDeliveryDate] = useState(null);
  const { products: allProductsData } = useProducts();
  const productsToUse = showAllProducts && allProductsData ? allProductsData : products;
  const { stockData, stockCarryOverData } = useStockData();
  const { priceLists } = usePriceLists();
  const { getCountriesMap, getCountryDelivery, getCountryDeliveryLabels, getDeliveryLabelLeadDays } = useCountries();
  const defaultDeliverySettings = getCountryDelivery(selectedCompany?.country || COMPANY_COUNTRY_CODE);
  const [selectedDeliveryDateForStock, setSelectedDeliveryDateForStock] = useState(null);

  // Delivery date -> packing dateId (DDMMYYYY), using the lead time
  // configured for the selected customer's country + delivery label.
  const getOrderPackingDateId = (deliveryDate) => {
    if (!deliveryDate) return null;
    const leadDays = getDeliveryLabelLeadDays(selectedCompany?.country || COMPANY_COUNTRY_CODE, deliveryLabel);
    return getPickingDate(deliveryDate, leadDays).format("DDMMYYYY");
  };

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  const isChefsFarmCustomer = useMemo(() => {
    if (selectedCompany) {
      return selectedCompany.company === CHEFS_FARM || !selectedCompany.company;
    }
    return companyFilter === CHEFS_FARM;
  }, [selectedCompany, companyFilter]);

  // Helper to get invoice surcharge percentage for the selected customer
  const getInvoiceSurchargePercentage = () => {
    if (!selectedCompany) return INVOICE_SURCHARGE_PERCENTAGE * 100;
    const countriesMap = getCountriesMap();
    const customerCountry = selectedCompany.country || "NL";
    const countrySettings = countriesMap?.[customerCountry]?.pricing;
    return countrySettings?.invoiceSurchargePercentage ?? INVOICE_SURCHARGE_PERCENTAGE * 100;
  };

  // Helpers for stock logic
  const isStockCarriedOver = (packingDateId) => stockCarryOverData[packingDateId] === true;

  const getStockInfo = (packingDateId, productId, source = stockData) => {
    return (
      source?.[packingDateId]?.[productId] || {
        currentStock: 0,
        projectedStock: 0,
        requestedStock: 0,
      }
    );
  };

  const getAvailableStock = (stockInfo, carriedOver) => {
    return carriedOver
      ? stockInfo.currentStock || 0
      : (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
  };

  // Calculations
  const newOrderSubtotal = useMemo(() => {
    if (!newOrderProducts || newOrderProducts.length === 0) return 0;

    return newOrderProducts.reduce((acc, item) => {
      const price = manualPriceOverrides[item.productId]
        ? parseFloat(manualPriceOverrides[item.productId])
        : parseFloat(item.productPrice);
      return acc + price * item.itemCount;
    }, 0);
  }, [newOrderProducts, manualPriceOverrides]);

  const newOrderSubtotalWithoutDiscount = useMemo(() => {
    return calculateOrderSubtotalWithoutDiscount(
      newOrderProducts,
      selectedCompany,
      productsToUse,
      priceLists,
      getCountriesMap(),
    );
  }, [newOrderProducts, productsToUse, selectedCompany, priceLists, getCountriesMap]);

  const newOrderCalculatedShippingCost = useMemo(() => {
    if (newOrderProducts?.length > 0 && selectedCompany) {
      return calculateOrderShippingCost(
        newOrderSubtotal,
        selectedCompany.id,
        customers,
        deliveryLabel,
        defaultDeliverySettings,
      );
    }
    return 0;
  }, [newOrderProducts, selectedCompany, customers, newOrderSubtotal, deliveryLabel, defaultDeliverySettings]);

  // (Re)set state when modal opens/closes
  useEffect(() => {
    if (visible) {
      resetState();
      form.setFieldsValue({
        deliveryDate: selectedDeliveryDate,
      });
      setCurrentDeliveryDate(selectedDeliveryDate);
      if (order) {
        const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
        const orderWithCustomer = { ...order, customer: dbCustomer };

        // Parse delivery time from order if available
        let timeSlotValue = null;
        if (order.deliveryTime && order.deliveryTime.includes("-")) {
          const [start, end] = order.deliveryTime.split("-");
          const timeSlot = {
            start: start.trim(),
            end: end.trim(),
            deliverySurcharge: order.deliveryTimeCharge || 0,
          };
          timeSlotValue = stringifyTimeSlot(timeSlot);
          setDeliveryTimeSlot(timeSlot);
        }

        form.setFieldsValue({
          ...orderWithCustomer,
          deliveryLabel: order.deliveryLabel ? order.deliveryLabel : dbCustomer?.deliveryLabel,
          deliveryTimeSlot: timeSlotValue,
          addShippingCost: !order.shippingCostDisabled,
          internalNotes: null,
        });
        if (order.shippingCostDisabled) {
          setAddShippingCost(false);
        } else {
          setAddShippingCost(true);
        }
        setNewOrderProducts(order.userShoppingCart);
        setSelectedCompany(dbCustomer);
        setDeliveryLabel(order.deliveryLabel ? order.deliveryLabel : dbCustomer?.deliveryLabel);
        setCurrentDeliveryDate(dayjs(order.farmorderDeliveryDate));
      } else {
        setNewOrderProducts([]);
        setProductsExceedingStock({});
      }
    }
  }, [visible, selectedDeliveryDate, order]);

  // No shipping costs for JLVO orders
  useEffect(() => {
    if (deliveryLabel === JLF_PICKED_UP) {
      setAddShippingCost(false);
      form.setFieldsValue({ addShippingCost: false });
    }
  }, [deliveryLabel, form]);

  // Clear and disable timeslot for ADD orders
  useEffect(() => {
    if (deliveryLabel === ADDITIONAL_DELIVERY_LABEL) {
      form.setFieldsValue({ deliveryTimeSlot: null });
      setDeliveryTimeSlot(null);
    }
  }, [deliveryLabel, form]);

  // Validate delivery day when date, label, or customer changes
  useEffect(() => {
    if (currentDeliveryDate && selectedCompany && deliveryLabel) {
      const warning = validateDeliveryDay(currentDeliveryDate, selectedCompany, deliveryLabel);
      setDeliveryDayWarning(warning);
    } else {
      setDeliveryDayWarning(null);
    }
  }, [currentDeliveryDate, selectedCompany, deliveryLabel]);

  const resetState = () => {
    form.resetFields();
    setSelectedCompany(null);
    setDeliveryLabel(
      selectedCompany?.company === GROWY
        ? RETAIL_DELIVERY_LABEL
        : defaultDeliverySettings?.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK,
    );
    setDeliveryTimeSlot(null);
    setAddShippingCost(true);
    setNewOrderProducts([]);
    setNewProduct("");
    setProductsExceedingStock({});
    setTempDeliveryDate(null);
    setLoading(false);
    setShowAllProducts(false);
    setManualPriceOverrides({});
    setSelectedDeliveryDateForStock(null);
    setDeliverySettingsWarning(null);
    setDeliveryDayWarning(null);
    setCurrentDeliveryDate(null);
  };

  // Validate delivery day against customer and delivery label settings
  const validateDeliveryDay = (date, customer, label) => {
    return validateDeliveryDayUtil(date, customer, label, getCountryDelivery, COMPANY_COUNTRY_CODE, t);
  };

  const validateAndUpdateDate = async (date) => {
    if (!date || !newOrderProducts || newOrderProducts.length === 0) return;

    try {
      const packingDateId = getOrderPackingDateId(date);
      const newStockData = stockData[packingDateId] || {};
      const stockCarriedOver = isStockCarriedOver(packingDateId);

      const unavailableProducts = [];
      const exceedingProducts = {};

      for (const product of newOrderProducts) {
        let originalQuantity = 0;
        if (order && order.userShoppingCart) {
          const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
          if (originalProduct) {
            originalQuantity = originalProduct.itemCount;
          }
        }

        const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: newStockData });

        const originalPackingDateId = getOrderPackingDateId(order?.farmorderDeliveryDate);

        const baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);
        const effectiveStock = baseAvailable + (packingDateId === originalPackingDateId ? originalQuantity : 0);

        if (product.itemCount > effectiveStock) {
          unavailableProducts.push({
            name: product.productName,
            ordered: product.itemCount,
            available: effectiveStock,
          });
          exceedingProducts[product.productId] = true;
        }
      }

      if (unavailableProducts.length > 0) {
        Modal.confirm({
          title: t("notifications.stock_issues_for_selected_date"),
          content: (
            <div>
              <p>{t("notifications.insufficient_stock_for_date")}</p>
              <ul style={{ margin: "0 0 20px 20px", padding: 0 }}>
                {unavailableProducts.map((product, index) => (
                  <li key={index}>
                    {product.name}: {t("ordered")} {product.ordered}, {t("available")} {product.available}
                  </li>
                ))}
              </ul>
              <p>{t("proceed_anyway_negative_stock_warning")}</p>
            </div>
          ),
          zIndex: 1100,
          okText: t("proceed_anyway"),
          cancelText: t("cancel"),
          onOk: () => {
            form.setFieldsValue({ deliveryDate: date });
            setTempDeliveryDate(date);
            setProductsExceedingStock(exceedingProducts);
          },
          onCancel: () => {
            form.setFieldsValue({ deliveryDate: tempDeliveryDate });
          },
        });
      } else {
        form.setFieldsValue({ deliveryDate: date });
        setTempDeliveryDate(date);
        setProductsExceedingStock({});
      }
    } catch (error) {
      console.error("Error checking stock for new date:", error);
      message.error(t("notifications.failed_check_stock"));
    }
  };

  const productDropdownOptions = useMemo(() => {
    const deliveryDate = selectedDeliveryDateForStock || form.getFieldValue("deliveryDate");
    if (!deliveryDate) return [];

    const packingDateId = getOrderPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    return [...productsToUse]
      ?.filter((item) => item.productInSeason === true)
      ?.filter((item) => !newOrderProducts.some((orderProduct) => orderProduct.productId === item.productId))
      ?.sort((a, b) => a.productName.localeCompare(b.productName))
      ?.map((product) => {
        const { productId, productName } = product;
        const stockInfo = getStockInfo(packingDateId, productId, stockData);
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);
        const isInStock = availableStock > 0;

        return {
          productId,
          productName,
          availableStock,
          isInStock,
        };
      });
  }, [productsToUse, newOrderProducts, selectedDeliveryDateForStock, stockData, stockCarryOverData, form]);

  const handleCompanyChange = (value) => {
    const company = customers.find((customer) => customer.customerId === value);
    if (!company) return;

    setSelectedCompany(company);

    // Get delivery settings for customer's country
    const customerDeliverySettings = getCountryDelivery(company.country || COMPANY_COUNTRY_CODE);

    // Validate if customer's delivery label + time slot combination still exists
    let validatedLabel = company.deliveryLabel;
    let validatedTimeSlot = company.deliveryTimeSlot;
    let showWarning = false;

    if (company.deliveryLabel && company.deliveryTimeSlot && customerDeliverySettings?.deliveryLabels) {
      // Check if the combination exists
      const labelConfig = customerDeliverySettings.deliveryLabels.find(
        (dl) => dl.deliveryLabel === company.deliveryLabel,
      );

      if (!labelConfig) {
        // Label doesn't exist anymore, use default
        validatedLabel = customerDeliverySettings.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK;
        validatedTimeSlot = null;
        showWarning = true;
      } else if (labelConfig.deliveryTimeSlots?.length > 0) {
        // Check if time slot exists for this label
        const timeSlotExists = labelConfig.deliveryTimeSlots.some(
          (slot) =>
            slot.start === company.deliveryTimeSlot.start &&
            slot.end === company.deliveryTimeSlot.end &&
            slot.deliverySurcharge === company.deliveryTimeSlot.deliverySurcharge,
        );

        if (!timeSlotExists) {
          // Time slot doesn't exist anymore, use first available
          validatedTimeSlot = labelConfig.deliveryTimeSlots[0];
          showWarning = true;
        }
      }
    } else if (!company.deliveryLabel || !company.deliveryTimeSlot) {
      // Use customer's label if available, otherwise use default
      validatedLabel =
        company.deliveryLabel ||
        (company.company === GROWY
          ? RETAIL_DELIVERY_LABEL
          : customerDeliverySettings?.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK);

      // Get first time slot for the label
      const labelConfig = customerDeliverySettings?.deliveryLabels?.find((dl) => dl.deliveryLabel === validatedLabel);
      if (labelConfig?.deliveryTimeSlots?.length > 0) {
        validatedTimeSlot = labelConfig.deliveryTimeSlots[0];
      }
    }

    // Show warning if configuration changed
    if (showWarning) {
      setDeliverySettingsWarning(t("notifications.customer_delivery_settings_no_longer_valid"));
    } else {
      setDeliverySettingsWarning(null);
    }

    // Set form values
    form.setFieldsValue({
      deliveryLabel: validatedLabel,
      deliveryTimeSlot: validatedTimeSlot ? stringifyTimeSlot(validatedTimeSlot) : null,
    });

    setDeliveryLabel(validatedLabel);
    setDeliveryTimeSlot(validatedTimeSlot);

    // Reset shopping cart when switching customers to avoid pricing issues
    setNewOrderProducts([]);
    setProductsExceedingStock({});

    // Reset user and reference fields first
    form.setFieldsValue({ farmorderUserId: null, reference: null });

    if (company && company.customerUsers.length > 0) {
      // Find the first valid user from customerUsers array
      let defaultUser = null;
      for (const userId of company.customerUsers) {
        const user = users.find(
          (user) =>
            user.userId === userId && user.userCustomerId === company.id && user.userStatus === USER_STATUS_ACTIVE,
        );
        if (user) {
          defaultUser = user;
          break;
        }
      }

      if (defaultUser) {
        form.setFieldsValue({ farmorderUserId: defaultUser.userId });
      }
    } else {
      form.setFieldsValue({ farmorderUserId: null });
    }

    // Set default reference if company has one
    if (company.defaultReference) {
      form.setFieldsValue({ reference: company.defaultReference });
    }
  };

  const addProductToNewOrder = (product) => {
    if (!selectedCompany) {
      message.warning(t("notifications.select_customer_first"));
      return;
    }

    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getOrderPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    const finalPrice = applyCustomerPricingToProduct(product, selectedCompany, priceLists, getCountriesMap());

    // Calculate bundle item prices for reporting purposes only
    // Note: The bundle price (finalPrice) is NEVER recalculated - it's a fixed price set via BundlePriceCalculator
    // Bundle items get individual prices only for accurate reporting and stock tracking
    let bundleItemsWithPricing = [];
    let packagingMaterialWithPricing = null;

    if (product.isBundle && product.bundleItems) {
      const allItemsWithPricing = getBundleItemsWithCustomerPricing(
        product,
        selectedCompany,
        finalPrice,
        productsToUse,
        priceLists,
        getCountriesMap(),
      );

      // Separate packaging items from regular bundle items
      bundleItemsWithPricing = allItemsWithPricing.filter((item) => !item.isPackaging);
      const packagingItem = allItemsWithPricing.find((item) => item.isPackaging);

      // Packaging keeps original price (no invoice surcharge or discount)
      if (packagingItem) {
        packagingMaterialWithPricing = packagingItem;
      } else if (product.packagingMaterial) {
        packagingMaterialWithPricing = product.packagingMaterial;
      }
    }

    const newProduct = {
      externalId: product.exactId?.toString() || "",
      itemCount: 1,
      orderable: true,
      productCategory: product.category?.en || "",
      productId: product.id,
      productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
      productName: product.productName,
      productPrice: finalPrice,
      productVat:
        product.vat && selectedCompany.country === "NL" ? product.vat[selectedCompany.country.toLowerCase()] : 0,
      productBarcode: product.barcode || "",
      // Store bundle pricing data if this is a bundle
      ...(product.isBundle && {
        isBundle: true,
        bundleItems: bundleItemsWithPricing,
        packagingMaterial: packagingMaterialWithPricing || product.packagingMaterial || null,
        hasCustomPrice: product.hasCustomPrice || false,
      }),
    };

    setNewOrderProducts((prevProducts) => [...prevProducts, newProduct]);

    const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

    if (availableStock < 1) {
      setProductsExceedingStock((prev) => ({
        ...prev,
        [product.id]: true,
      }));
    }
  };

  const increaseQuantity = (product) => {
    const index = newOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(newOrderProducts);
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getOrderPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    if (index !== -1) {
      newProducts[index].itemCount += 1;

      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (newProducts[index].itemCount > availableStock) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      } else {
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    } else {
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (availableStock <= 0) {
        message.warning(t("notifications.product_exceeds_available_stock"));
      }

      const finalPrice = applyCustomerPricingToProduct(product, selectedCompany, priceLists, getCountriesMap());

      // Calculate bundle item prices for reporting purposes only
      // Note: The bundle price (finalPrice) is NEVER recalculated - it's a fixed price set via BundlePriceCalculator
      let bundleItemsWithPricing = [];
      let packagingMaterialWithPricing = null;

      if (product.isBundle && product.bundleItems) {
        const allItemsWithPricing = getBundleItemsWithCustomerPricing(
          product,
          selectedCompany,
          finalPrice,
          productsToUse,
          priceLists,
          getCountriesMap(),
        );

        // Separate packaging items from regular bundle items
        bundleItemsWithPricing = allItemsWithPricing.filter((item) => !item.isPackaging);
        const packagingItem = allItemsWithPricing.find((item) => item.isPackaging);

        // Packaging keeps original price (no invoice surcharge or discount)
        if (packagingItem) {
          packagingMaterialWithPricing = packagingItem;
        } else if (product.packagingMaterial) {
          packagingMaterialWithPricing = product.packagingMaterial;
        }
      }

      newProducts.push({
        externalId: product.exactId?.toString() || "",
        itemCount: 1,
        orderable: true,
        productCategory: product.category?.en || "",
        productId: product.productId,
        productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
        productName: product.productName,
        productPrice: finalPrice,
        productVat:
          product.vat && selectedCompany.country === "NL" ? product.vat[selectedCompany.country.toLowerCase()] : 0,
        productBarcode: product.barcode || "",
        // Store bundle pricing data if this is a bundle
        ...(product.isBundle && {
          isBundle: true,
          bundleItems: bundleItemsWithPricing,
          packagingMaterial: packagingMaterialWithPricing || product.packagingMaterial || null,
          calculatedItemPrices: product.calculatedItemPrices || {},
          hasCustomPrice: product.hasCustomPrice || false,
        }),
      });

      if (availableStock <= 0) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      }
    }

    setNewOrderProducts(newProducts);
  };

  const decreaseQuantity = (product) => {
    const index = newOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(newOrderProducts);

    if (index !== -1) {
      newProducts[index].itemCount -= 1;

      if (productsExceedingStock[product.productId]) {
        const deliveryDate = form.getFieldValue("deliveryDate");
        const packingDateId = getOrderPackingDateId(deliveryDate);
        const stockCarriedOver = isStockCarriedOver(packingDateId);
        const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

        if (newProducts[index].itemCount <= availableStock) {
          setProductsExceedingStock((prev) => {
            const newState = { ...prev };
            delete newState[product.productId];
            return newState;
          });
        }
      }

      if (newProducts[index].itemCount === 0) {
        newProducts.splice(index, 1);
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setNewOrderProducts(newProducts);
  };

  const validateStock = () => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getOrderPackingDateId(deliveryDate);
    const latestStockData = stockData[packingDateId] || {};
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    const stockErrors = [];
    for (const product of newOrderProducts) {
      const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: latestStockData });
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (product.itemCount > availableStock) {
        stockErrors.push({
          name: product.productName,
          ordered: product.itemCount,
          available: availableStock,
        });
      }
    }

    return stockErrors;
  };

  // Operation tracking functions
  const showOperationSummary = (results) => {
    const successfulOps = [];
    const failedOps = [];
    const partialOps = [];

    // Handle firestore and stock operations
    Object.entries(results).forEach(([operation, result]) => {
      if (!result.attempted) return;

      if (operation === "orderProcessing" && result.details) {
        // Handle detailed order processing operations
        Object.entries(result.details).forEach(([subOperation, subResult]) => {
          if (!subResult.attempted) return;

          if (subOperation === "email" && selectedCompany?.company === GROWY) {
            return;
          }

          const operationName = getDetailedOperationDisplayName(subOperation);

          if (subResult.success && !subResult.error) {
            successfulOps.push(operationName);
          } else if (subResult.success && subResult.error) {
            partialOps.push({ operation: operationName, error: subResult.error });
          } else {
            failedOps.push({ operation: operationName, error: subResult.error });
          }
        });
      } else if (operation !== "orderProcessing") {
        // Handle firestore and stock operations
        if (result.success && !result.error) {
          successfulOps.push(getOperationDisplayName(operation));
        } else if (result.success && result.error) {
          partialOps.push({ operation: getOperationDisplayName(operation), error: result.error });
        } else {
          failedOps.push({ operation: getOperationDisplayName(operation), error: result.error });
        }
      }
    });

    const hasFailures = failedOps.length > 0;
    const hasPartial = partialOps.length > 0;

    const title = hasFailures
      ? t("notifications.order_creation_partial")
      : hasPartial
        ? t("notifications.order_creation_with_warnings")
        : t("notifications.order_creation_complete");

    const content = (
      <div>
        {successfulOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#52c41a" }}>{t("successful_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {successfulOps.map((op, index) => (
                <li key={index}>{op}</li>
              ))}
            </ul>
          </div>
        )}

        {partialOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#faad14" }}>{t("partial_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {partialOps.map(({ operation, error }, index) => (
                <li key={index}>
                  {operation}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {failedOps.length > 0 && (
          <div>
            <strong style={{ color: "#ff4d4f" }}>{t("failed_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {failedOps.map(({ operation, error }, index) => (
                <li key={index}>
                  {operation}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    notification.destroy();

    notification[hasFailures ? "error" : hasPartial ? "warning" : "success"]({
      message: title,
      description: content,
      duration: 0,
      style: { width: "400px" },
    });
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      firestore: t("order_created_database"),
      stock: t("stock_updated"),
    };
    return names[operation] || operation;
  };

  const getDetailedOperationDisplayName = (operation) => {
    const names = {
      farmorderId: t("order_id_updated"),
      email: t("email_sent_to_customer"),
      discountCode: t("notifications.discount_code_processed"),
      sooCool: t("notifications.soocool_delivery_created"),
    };
    return names[operation] || operation;
  };

  const handleSubmit = (bypassCheck = false) => {
    form.validateFields().then(async (values) => {
      try {
        if (newOrderProducts.length === 0) {
          message.error(t("notifications.add_products_first"));
          return;
        }

        setLoading(true);

        // Initialize operation results
        const operationResults = {
          firestore: { attempted: true, success: false, error: null },
          orderProcessing: { attempted: false, success: false, error: null, details: null },
          stock: { attempted: false, success: false, error: null },
        };

        // Validate stock unless bypassed
        if (!bypassCheck) {
          const stockErrors = validateStock();
          if (stockErrors.length) {
            Modal.confirm({
              title: t("notifications.stock_validation_failed"),
              content: (
                <div>
                  <p>{t("notifications.products_exceed_stock")}</p>
                  <ul>
                    {stockErrors.map((error, index) => (
                      <li key={index}>
                        {error.name}: {t("ordered")} {error.ordered}, {t("available")} {error.available}
                      </li>
                    ))}
                  </ul>
                  <p>{t("proceed_anyway_negative_stock_warning")}</p>
                </div>
              ),
              okText: t("proceed_anyway"),
              cancelText: t("cancel"),
              onOk: () => handleSubmit(true),
              onCancel: () => setLoading(false),
            });
            return;
          }
        }

        // Create order object
        const company = customers.find((customer) => customer.customerId === values.farmorderCustomerId);
        const user = users.find((user) => user.userId === values.farmorderUserId);

        // Get delivery time and charge from selected time slot
        let deliveryTime = DEFAULT_DELIVERY_TIME;
        let deliveryTimeCharge = 0;
        let deliveryTimeSlotObject = null;

        // ADD orders don't have timeslots
        if (deliveryLabel === ADDITIONAL_DELIVERY_LABEL) {
          deliveryTime = DEFAULT_DELIVERY_TIME;
          deliveryTimeCharge = 0;
          deliveryTimeSlotObject = null;
        } else {
          const selectedTimeSlotValue = form.getFieldValue("deliveryTimeSlot");
          if (selectedTimeSlotValue) {
            try {
              const timeSlot = JSON.parse(selectedTimeSlotValue);
              deliveryTime = `${timeSlot.start}-${timeSlot.end}`;
              deliveryTimeCharge = timeSlot.deliverySurcharge || 0;
              deliveryTimeSlotObject = timeSlot; // Store the full object
            } catch (e) {
              console.error("Error parsing time slot:", e);
              // Fallback to old logic
              if (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel)) {
                deliveryTime = MORNING_DELIVERY_TIME;
                deliveryTimeCharge = MORNING_DELIVERY_CHARGE;
              }
            }
          } else if (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel)) {
            // Fallback to old logic if no time slot selected
            deliveryTime = MORNING_DELIVERY_TIME;
            deliveryTimeCharge = MORNING_DELIVERY_CHARGE;
          }
        }

        // Apply manual price overrides to products before calculating pricing
        const productsWithOverrides = newOrderProducts.map((item) => {
          const overridePrice = manualPriceOverrides[item.productId];
          return {
            ...item,
            productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : item.productPrice,
            ...(item.isBundle &&
              overridePrice && {
                bundleItems: recalculateBundleItemPricesForOverride(item, overridePrice),
              }),
          };
        });

        // Calculate pricing using the same function as EditOrderModal and OrderPickingDetail
        const pricing = calculateOrderPricing(
          productsWithOverrides,
          company,
          deliveryLabel,
          products,
          addShippingCost,
          null, // no discount code
          priceLists,
          getCountriesMap(),
          defaultDeliverySettings,
        );

        const subTotalAmount = pricing.subtotal;
        const subTotalWithoutDiscount = pricing.subtotalWithoutDiscount;
        const shippingCost = pricing.shippingCost;
        const totalAmount = pricing.total + deliveryTimeCharge;

        const orderObj = {
          farmorderCustomerId: company.customerId,
          farmorderCustomerName: company.customerName,
          customerDiscount: company.customerSnelstartDiscount || 0,
          priceListId: company.priceListId || null,
          farmorderRegion: company.customerRegion,
          deliveryCostThreshold: company.deliveryCostThreshold ?? defaultDeliverySettings?.deliveryCostThreshold,
          paymentMethod: company.paymentMethod,
          deliveryTime: deliveryTime,
          deliveryTimeCharge: deliveryTimeCharge,
          deliveryTimeSlot: deliveryTimeSlotObject, // Store the full timeslot object
          deliveryNotes: company.deliveryNotes || "",
          deliveryLabel: deliveryLabel,
          farmorderComments: values.farmorderComments || "",
          farmorderDeliveryDate: dayjs(values.deliveryDate).format("YYYY-MM-DD"),
          farmorderStatus: ORDER_STATUS_CREATED,
          farmorderUserFirstName: user?.userFirstName || "",
          farmorderUserLastName: user?.userLastName || "",
          userLanguage: user?.userLanguage || "en",
          userEmail: selectedCompany.company === GROWY ? "n/a - Growy order" : user?.userEmail || "",
          farmorderUserId: user?.userId || "",
          deliveryBy: selectedCompany.company === GROWY ? "Growy" : "Chef's Farm",
          farmorderDateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
          userShoppingCart: productsWithOverrides,
          orderDateTime: dayjs().unix() * 1000,
          subTotalAmount: subTotalAmount,
          subTotalWithoutDiscount: subTotalWithoutDiscount,
          invoiceAdditionalCosts: pricing.invoiceAdditionalCosts,
          shippingCost: shippingCost.toString(),
          totalAmount: totalAmount,
          tax: pricing.tax,
          createdBy: `CMS - ${email}`,
          shippingCostDisabled: !addShippingCost,
          reference: values.reference || "",
        };

        if (values.farmorderComments) {
          orderObj.orderCommentsProcessed = false;
        }

        if (values.internalNotes) {
          orderObj.internalNotes = {
            text: values.internalNotes,
            createdAt: dayjs().unix(),
            createdBy: email,
          };
        }

        // SAVE TO FIRESTORE
        try {
          const docRef = await addDoc(
            collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
            orderObj,
          );

          const farmorderId = docRef.id;

          // Update with order ID
          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, farmorderId),
            { farmorderId: farmorderId, updatedAt: dayjs().unix(), updatedBy: email },
          );

          // SENDING ORDERS TO API
          operationResults.orderProcessing = { attempted: true, success: false, error: null, details: null };
          try {
            const processToken = await getFirebaseIDToken();

            const processResponse = await axios.post(
              urls.processOrder,
              { ...orderObj, farmorderId, id: farmorderId },
              {
                headers: { Authorization: processToken },
              },
            );

            const processData = processResponse.data;
            operationResults.orderProcessing.success = processData.success || false;
            operationResults.orderProcessing.details = processData.operations || {};

            if (!processData.success) {
              operationResults.orderProcessing.error = processData.message || "Process order failed";
            }
          } catch (processError) {
            console.error("Process order error:", processError);
            operationResults.orderProcessing.error = processError.response?.data?.message || processError.message;
          }

          // STOCK API
          operationResults.stock = { attempted: true, success: false, error: null };
          try {
            const processToken = await getFirebaseIDToken();

            const stockResponse = await axios.post(
              urls.stockApiUrl,
              { ...orderObj, farmorderId, id: farmorderId },
              {
                headers: { Authorization: processToken },
              },
            );

            const stockData = stockResponse.data;
            if (stockData && typeof stockData.success === "boolean") {
              operationResults.stock.success = stockData.success;
              if (!stockData.success) {
                operationResults.stock.error = stockData.message || "Stock update returned an error";
              }
            } else {
              operationResults.stock.success = true;
            }
          } catch (stockError) {
            console.error("Stock API error:", stockError);
            operationResults.stock.error = stockError.response?.data?.message || stockError.message;
          }

          operationResults.firestore.success = true;
        } catch (firestoreError) {
          console.error("Firestore error:", firestoreError);
          operationResults.firestore.error = firestoreError.message;
          setLoading(false);
          showOperationSummary(operationResults);
          return;
        }

        // Show operation summary
        showOperationSummary(operationResults);
        resetState();
        onClose();
      } catch (error) {
        console.error("Error creating order:", error);
        message.error(t("notifications.error_creating_order") + ": " + error.message);
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <Modal
      title={t("create_order")}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => handleSubmit(false)}>
          {t("save")}
        </Button>,
      ]}
      width={600}
      centered
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          customerDiscount: 0,
          country: PRODUCT_STOCK_COUNTRIES[0].code,
          deliveryDays: DELIVERY_DAYS,
        }}
        onValuesChange={(changedValues) => {
          if (Object.keys(changedValues).includes("addShippingCost")) {
            setAddShippingCost(changedValues.addShippingCost);
          }
        }}
      >
        <Row gutter={16}>
          <Col lg={12}>
            <Form.Item
              name="farmorderCustomerId"
              label={isChefsFarmCustomer ? t("restaurant_name") : t("user.company")}
              rules={[{ required: true, message: t("form.enter_customer_name") }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                allowClear
                placeholder={isChefsFarmCustomer ? t("form.choose_restaurant") : t("form.choose_company")}
                onChange={handleCompanyChange}
              >
                {customers &&
                  [...customers]
                    .sort((a, b) => {
                      if (!a.customerName || !b.customerName) return 0;
                      return a.customerName.localeCompare(b.customerName);
                    })
                    .map((company) => (
                      <Option key={company.customerId} value={company.customerId}>
                        {company.customerName}
                      </Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item
              name="farmorderUserId"
              label={t("user.company_user")}
              rules={[
                {
                  required: selectedCompany?.company !== GROWY,
                  message: t("form.enter_user_name"),
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                allowClear
                placeholder={t("form.choose_user")}
                disabled={!selectedCompany?.id}
              >
                {users &&
                  users
                    .filter((user) => user.userCustomerId === selectedCompany?.id)
                    .map((user) => (
                      <Option key={user.userId} value={user.userId} disabled={user.userStatus !== USER_STATUS_ACTIVE}>
                        {user.userFirstName} {user.userLastName}
                      </Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {deliverySettingsWarning && (
          <Alert
            message={t("notifications.delivery_settings_changed")}
            description={deliverySettingsWarning}
            type="warning"
            showIcon
            closable
            onClose={() => setDeliverySettingsWarning(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider style={{ marginTop: 0 }} />

        {deliveryDayWarning && (
          <Alert
            message={deliveryDayWarning}
            type="warning"
            showIcon
            closable
            onClose={() => setDeliveryDayWarning(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryDate"
              label={t("delivery_date")}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                onChange={(date) => {
                  setCurrentDeliveryDate(date);
                  validateAndUpdateDate(date);
                  setSelectedDeliveryDateForStock(date);
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryLabel"
              label={t("delivery_label")}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <Select
                onChange={(value) => {
                  setDeliveryLabel(value);
                  // Auto-set first time slot when label changes
                  if (selectedCompany) {
                    const companyDeliverySettings = getCountryDelivery(selectedCompany.country || COMPANY_COUNTRY_CODE);
                    const labelConfig = companyDeliverySettings?.deliveryLabels?.find(
                      (dl) => dl.deliveryLabel === value,
                    );
                    if (labelConfig?.deliveryTimeSlots?.length > 0) {
                      const firstSlot = labelConfig.deliveryTimeSlots[0];
                      form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(firstSlot));
                      setDeliveryTimeSlot(firstSlot);
                    }
                  }
                }}
                value={deliveryLabel}
              >
                {getCountryDeliveryLabels(selectedCompany?.country).map((d) => (
                  <Select.Option value={d.code} key={d.code}>
                    {d.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryTimeSlot"
              label={t("delivery_time_slot")}
              rules={[
                {
                  required: deliveryLabel !== ADDITIONAL_DELIVERY_LABEL,
                  message: t("form.select_delivery_time_slot"),
                },
              ]}
            >
              <Select
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabel || !selectedCompany || deliveryLabel === ADDITIONAL_DELIVERY_LABEL}
                onChange={(value) => {
                  try {
                    const slot = JSON.parse(value);
                    setDeliveryTimeSlot(slot);
                  } catch (e) {
                    console.error("Error parsing time slot:", e);
                  }
                }}
                optionLabelProp="label"
              >
                {deliveryLabel &&
                  selectedCompany &&
                  (() => {
                    const companyDeliverySettings = getCountryDelivery(selectedCompany.country || COMPANY_COUNTRY_CODE);
                    return companyDeliverySettings?.deliveryLabels
                      ?.find((dl) => dl.deliveryLabel === deliveryLabel)
                      ?.deliveryTimeSlots?.map((slot, index) => {
                        const label = `${slot.start} - ${slot.end}${
                          slot.deliverySurcharge > 0 ? ` (+€${slot.deliverySurcharge})` : ""
                        }`;
                        return (
                          <Select.Option key={index} value={stringifyTimeSlot(slot)} label={label}>
                            {label}
                          </Select.Option>
                        );
                      });
                  })()}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item name="addShippingCost" label={t("enable_shipping_cost")} valuePropName="checked">
              <Switch
                checkedChildren={t("yes")}
                unCheckedChildren={t("no")}
                defaultChecked={true}
                disabled={deliveryLabel === "JLVO"}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Form.Item name="reference" label={t("reference")}>
              <Input placeholder={t("form.enter_reference")} maxLength={20} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name={"internalNotes"} label={t("internal_notes")}>
              <Input.TextArea placeholder={t("internal_order_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <h3>{t("products")}</h3>
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch checked={showAllProducts} onChange={setShowAllProducts} size="small" />
          <span style={{ fontSize: "14px" }}>{t("show_all_products")}</span>
        </div>

        {newOrderProducts?.map((product) => {
          const { productId, productName, itemCount } = product;
          const isExceedingStock = productsExceedingStock[productId];

          return (
            <EditOrderCartItem
              key={productId}
              style={
                isExceedingStock
                  ? {
                      borderLeft: "3px solid #ff4d4f",
                      paddingLeft: "8px",
                      backgroundColor: "rgba(255, 77, 79, 0.05)",
                    }
                  : {}
              }
            >
              <div style={{ display: "flex" }}>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const existingProductIndex = newOrderProducts.findIndex((item) => item.productId === productId);
                    if (existingProductIndex > -1) {
                      const newProducts = [...newOrderProducts];
                      newProducts.splice(existingProductIndex, 1);
                      setNewOrderProducts(newProducts);
                      // Remove from exceeding stock tracking
                      setProductsExceedingStock((prev) => {
                        const newState = { ...prev };
                        delete newState[productId];
                        return newState;
                      });
                    }
                  }}
                  style={{ marginRight: "5px" }}
                />
                <div>
                  <h4>{productName}</h4>

                  {/* Price editor */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                    {manualPriceOverrides[productId] !== undefined ? (
                      // Edit mode - show input
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          size="small"
                          style={{ width: "80px" }}
                          prefix="€"
                          value={manualPriceOverrides[productId]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || parseFloat(value) >= 0) {
                              setManualPriceOverrides((prev) => ({
                                ...prev,
                                [productId]: value,
                              }));
                            }
                          }}
                          autoFocus
                          onBlur={() => {
                            if (!manualPriceOverrides[productId]) {
                              setManualPriceOverrides((prev) => {
                                const newOverrides = { ...prev };
                                delete newOverrides[productId];
                                return newOverrides;
                              });
                            } else if (product.isBundle) {
                              const packagingPrice = getBundlePackagingPrice(product);
                              if (parseFloat(manualPriceOverrides[productId]) < packagingPrice) {
                                setManualPriceOverrides((prev) => ({
                                  ...prev,
                                  [productId]: packagingPrice.toFixed(2),
                                }));
                              }
                            }
                          }}
                        />
                        <span style={{ fontSize: "12px", color: "#999", textDecoration: "line-through" }}>
                          €{formatPrice(parseFloat(product.productPrice))}
                        </span>
                        {product.isBundle && getBundlePackagingPrice(product) > 0 && (
                          <span style={{ fontSize: "11px", color: "#888" }}>
                            ({t("packaging")}: €{formatPrice(getBundlePackagingPrice(product))})
                          </span>
                        )}
                        {product.isBundle &&
                          manualPriceOverrides[productId] !== "" &&
                          parseFloat(manualPriceOverrides[productId]) < getBundlePackagingPrice(product) && (
                            <span style={{ fontSize: "11px", color: "#ff4d4f" }}>
                              {t("bundle_price_below_packaging")}
                            </span>
                          )}
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setManualPriceOverrides((prev) => {
                              const newOverrides = { ...prev };
                              delete newOverrides[productId];
                              return newOverrides;
                            });
                          }}
                          style={{ padding: "0 4px", fontSize: "12px" }}
                        >
                          {t("reset")}
                        </Button>
                      </>
                    ) : (
                      // Display mode - clickable price
                      <span
                        onClick={() => {
                          setManualPriceOverrides((prev) => ({
                            ...prev,
                            [productId]: product.productPrice,
                          }));
                        }}
                        style={{
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        €{formatPrice(parseFloat(product.productPrice))}
                      </span>
                    )}
                  </div>

                  {isExceedingStock && (
                    <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                      {t("exceeds_available_stock")}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <QuantityControl
                  productId={productId}
                  currentQuantity={itemCount}
                  onIncrease={() => increaseQuantity(product)}
                  onDecrease={() => decreaseQuantity(product)}
                  onManualInput={(productId, value) => {
                    const product = productsToUse.find((p) => p.productId === productId);
                    const existingProductIndex = newOrderProducts.findIndex((item) => item.productId === productId);
                    const deliveryDate = form.getFieldValue("deliveryDate");
                    const packingDateId = getOrderPackingDateId(deliveryDate);
                    const stockCarriedOver = stockCarryOverData[packingDateId] === true;
                    const stockInfo = stockData[packingDateId]?.[productId] || {
                      currentStock: 0,
                      projectedStock: 0,
                      requestedStock: 0,
                    };

                    if (value === 0) {
                      if (existingProductIndex > -1) {
                        const newProducts = [...newOrderProducts];
                        newProducts.splice(existingProductIndex, 1);
                        setNewOrderProducts(newProducts);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    } else if (existingProductIndex > -1) {
                      const newProducts = [...newOrderProducts];
                      newProducts[existingProductIndex].itemCount = value;
                      setNewOrderProducts(newProducts);

                      let availableStock;
                      if (stockCarriedOver) {
                        availableStock = stockInfo.currentStock;
                      } else {
                        availableStock = (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
                      }

                      if (value > availableStock) {
                        setProductsExceedingStock((prev) => ({
                          ...prev,
                          [productId]: true,
                        }));
                      } else {
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    } else {
                      const finalPrice = applyCustomerPricingToProduct(
                        product,
                        selectedCompany,
                        priceLists,
                        getCountriesMap(),
                      );
                      const newProduct = {
                        externalId: product.exactId?.toString() || "",
                        itemCount: value,
                        orderable: true,
                        productCategory: product.category?.en || "",
                        productId: product.productId,
                        productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
                        productName: product.productName,
                        productPrice: finalPrice,
                        // Store bundle pricing data if this is a bundle
                        ...(product.isBundle && {
                          isBundle: true,
                          bundleItems: product.bundleItems || [],
                          packagingMaterial: product.packagingMaterial || null,
                          calculatedItemPrices: product.calculatedItemPrices || {},
                          hasCustomPrice: product.hasCustomPrice || false,
                        }),
                      };
                      setNewOrderProducts((prev) => [...prev, newProduct]);

                      let availableStock;
                      if (stockCarriedOver) {
                        availableStock = stockInfo.currentStock;
                      } else {
                        availableStock = (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
                      }

                      if (value > availableStock) {
                        setProductsExceedingStock((prev) => ({
                          ...prev,
                          [productId]: true,
                        }));
                      }
                    }
                  }}
                />
              </div>
            </EditOrderCartItem>
          );
        })}

        <NewProductsWrapper>
          <Select
            style={{ width: "100%" }}
            placeholder={t("add_products")}
            showSearch
            filterOption={(input, option) => {
              const productData = productDropdownOptions.find((p) => p.productId === option.value);
              return productData?.productName?.toLowerCase().includes(input.toLowerCase());
            }}
            onChange={(value) => {
              if (value) {
                const product = productsToUse?.find((item) => item.productId === value);
                if (newOrderProducts.length === 0) {
                  addProductToNewOrder(product);
                } else {
                  increaseQuantity(product);
                }
                setNewProduct(null);
              }
            }}
            value={newProduct}
          >
            {productDropdownOptions.map((productData) => (
              <Select.Option key={productData.productId} value={productData.productId}>
                {productData.productName}
                {productData.isInStock ? (
                  ` (${t("stock")}: ${productData.availableStock})`
                ) : (
                  <span style={{ color: "#ff4d4f" }}>{` (${t("stock")}: ${productData.availableStock})`}</span>
                )}
              </Select.Option>
            ))}
          </Select>
          {process.env.REACT_APP_ENVIRONMENT !== "production" && (
            <Button
              style={{ marginTop: "8px", width: "100%" }}
              type="dashed"
              onClick={() => {
                const availableProducts = [...productsToUse]
                  ?.filter((item) => item.productInSeason === true)
                  ?.filter(
                    (item) => !newOrderProducts.some((orderProduct) => orderProduct.productId === item.productId),
                  );

                const newProducts = availableProducts.map((product) => {
                  const finalPrice = applyCustomerPricingToProduct(
                    product,
                    selectedCompany,
                    priceLists,
                    getCountriesMap(),
                  );
                  return {
                    externalId: product.exactId?.toString() || "",
                    itemCount: 1,
                    orderable: true,
                    productCategory: product.category?.en || "",
                    productId: product.id,
                    productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
                    productName: product.productName,
                    productPrice: finalPrice,
                    // Store bundle pricing data if this is a bundle
                    ...(product.isBundle && {
                      isBundle: true,
                      bundleItems: product.bundleItems || [],
                      packagingMaterial: product.packagingMaterial || null,
                      calculatedItemPrices: product.calculatedItemPrices || {},
                      hasCustomPrice: product.hasCustomPrice || false,
                    }),
                  };
                });

                setNewOrderProducts((prev) => [...prev, ...newProducts]);
              }}
            >
              [DEV ONLY] Add All Products
            </Button>
          )}
        </NewProductsWrapper>

        {/* Price Summary */}
        {addShippingCost ? (
          <>
            <PriceRow style={{ marginTop: "1.5rem" }}>
              <h4 style={{ fontWeight: "400" }}>{t("sub_total")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(newOrderSubtotal)}</h4>
            </PriceRow>
            {selectedCompany?.paymentMethod === PAYMENT_METHOD_INVOICE &&
              selectedCompany?.customerId !== JIMMY_LOVES_FOOD_ID &&
              selectedCompany?.company !== GROWY &&
              !selectedCompany?.priceListId && (
                <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                  {t("invoice_payment_surcharge")} +{getInvoiceSurchargePercentage()}%
                </p>
              )}
            {selectedCompany?.customerSnelstartDiscount > 0 && !selectedCompany?.priceListId && (
              <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                {t("customer_discount")} -{selectedCompany.customerSnelstartDiscount}%
              </p>
            )}
            <PriceRow>
              <h4 style={{ fontWeight: "400" }}>{t("shipping")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(newOrderCalculatedShippingCost)}</h4>
            </PriceRow>
            {(() => {
              // Use deliverySurcharge from selected time slot, fallback to old MORNING_DELIVERY_CHARGE logic
              const surcharge =
                deliveryTimeSlot?.deliverySurcharge ||
                (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0);

              if (surcharge > 0) {
                return (
                  <PriceRow>
                    <h4 style={{ fontWeight: "400" }}>{t("delivery_time_surcharge")}</h4>
                    <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(surcharge)}</h4>
                  </PriceRow>
                );
              }
              return null;
            })()}
            <PriceRow style={{ marginBottom: 0 }}>
              <h4>{t("total")}</h4>
              <h4>
                {CURRENCY +
                  formatPrice(
                    newOrderSubtotal +
                      newOrderCalculatedShippingCost +
                      (deliveryTimeSlot?.deliverySurcharge ||
                        (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0)),
                  )}
              </h4>
            </PriceRow>
            {newOrderCalculatedShippingCost > 0 && (
              <p>
                {t("order_amount_missing_for_free_delivery", {
                  price: formatPrice(
                    (selectedCompany?.deliveryCostThreshold ||
                      defaultDeliverySettings?.deliveryCostThreshold ||
                      FREE_DELIVERY_FROM_PRICE) - newOrderSubtotal,
                  ),
                })}
              </p>
            )}
          </>
        ) : (
          <div style={{ marginTop: "1.5rem" }}>
            <PriceRow style={{ marginTop: "1.5rem" }}>
              <h4 style={{ fontWeight: "400" }}>{t("sub_total")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(newOrderSubtotal)}</h4>
            </PriceRow>
            {selectedCompany?.paymentMethod === PAYMENT_METHOD_INVOICE && !selectedCompany?.priceListId && (
              <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                {t("invoice_payment_surcharge")} +{getInvoiceSurchargePercentage()}%
              </p>
            )}
            {selectedCompany?.customerSnelstartDiscount > 0 && !selectedCompany?.priceListId && (
              <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                {t("customer_discount")} -{selectedCompany.customerSnelstartDiscount}%
              </p>
            )}
            {MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) && (
              <PriceRow>
                <h4 style={{ fontWeight: "400" }}>{t("morning_shipping_cost_extra")}</h4>
                <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(MORNING_DELIVERY_CHARGE)}</h4>
              </PriceRow>
            )}
            <PriceRow>
              <h4>{t("total")}</h4>
              <h4>
                {CURRENCY +
                  formatPrice(
                    newOrderSubtotal +
                      (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0),
                  )}
              </h4>
            </PriceRow>
          </div>
        )}
      </Form>
    </Modal>
  );
};

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h4 {
    margin-bottom: 0;
  }
`;

const NewProductsWrapper = styled.div`
  .ant-collapse-content-box {
    padding-inline: 0 !important;
  }
`;

const EditOrderCartItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

  h4 {
    margin: 0;
    font-size: 16px;
    line-height: 1.2;
  }

  p {
    margin: 0;
  }

  .quantity-chooser {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgb(232, 232, 232);
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1;
      padding-inline: 6px;
      min-width: 50px;
      text-align: center;
      color: #1a3353;
    }

    .anticon {
      font-size: 12px;
      padding: 8px 6px;
      cursor: pointer;
    }
  }
`;

export default AddOrderModal;
